/**
 * A receipt photo, made small enough to send on the tablet.
 *
 * "Photos to JPEG under ~300KB, on the device, before upload." A phone camera
 * gives 3–12MB; a receipt is legible at 1600px on its long side. Scale to that,
 * then step the JPEG quality down until it fits. Everything happens in a canvas
 * here — nothing leaves the tablet until it is small.
 */

export const MAX_RECEIPT_BYTES = 300_000;
const MAX_EDGE = 1600;
const QUALITIES = [0.82, 0.7, 0.58, 0.46, 0.35];

export async function compressPhoto(file: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not prepare the photo.');
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    let smallest: Blob | null = null;
    for (const quality of QUALITIES) {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/jpeg', quality),
      );
      if (!blob) continue;
      if (blob.size <= MAX_RECEIPT_BYTES) return blob;
      smallest = blob;
    }
    if (smallest) return smallest;
    throw new Error('Could not prepare the photo.');
  } finally {
    bitmap.close();
  }
}
